import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';

import { BidetModal } from './components/BidetModal';
import { StandardPage } from './components/StandardPage';
import { OrderProvider, useOrder } from './contexts/OrderContext';
import { ProductProvider } from './contexts/ProductContext';
import { SiteConfigProvider } from './contexts/SiteConfigContext';
import React, { useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { StagingPasswordWall } from './components/StagingPasswordWall';
import type { PageMetadata } from './routes';
import { metadataHandleForPath } from './routeMetadata';
import type { Route } from './+types/root';
import './app.css';

// this exports the links function for preloading fonts
// used by the <Links /> component, indirectly
export const links: Route.LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
];

// Customer PII — name, email, phone, address — flows through OrderContext
// during checkout and through admin views. We must never ship it to Sentry.
const PII_KEY_RE = /email|phone|first_?name|last_?name|full_?name|address|recipient|street|zip|postal|city|state|country|ssn|dob|birth/i;

function scrubPii(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[depth]';
  if (value == null) return value;
  if (typeof value === 'string') {
    return value
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]')
      .replace(/\+?\d[\d\s().-]{8,}\d/g, '[phone]');
  }
  if (Array.isArray(value)) return value.map(v => scrubPii(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = PII_KEY_RE.test(k) ? '[redacted]' : scrubPii(v, depth + 1);
    }
    return out;
  }
  return value;
}

if (typeof window !== 'undefined') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: import.meta.env.VITE_ENV === 'dev' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (event.request) event.request = scrubPii(event.request) as typeof event.request;
      if (event.extra) event.extra = scrubPii(event.extra) as typeof event.extra;
      if (event.contexts) event.contexts = scrubPii(event.contexts) as typeof event.contexts;
      if (event.user) event.user = { id: event.user.id };
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) breadcrumb.data = scrubPii(breadcrumb.data) as typeof breadcrumb.data;
      if (breadcrumb.message) breadcrumb.message = scrubPii(breadcrumb.message) as string;
      return breadcrumb;
    },
  });
}

// Add this export for react-router's meta handling
export function meta({ location, matches }: Route.MetaArgs) {
  const currentRouteMatch = matches[matches.length - 1];
  const routeMetadata =
    (currentRouteMatch?.handle as { metadata?: PageMetadata })?.metadata
    ?? metadataHandleForPath(location.pathname).metadata;

  const defaultMetadata: PageMetadata = {
    title: 'BidetNY - Modern Hygiene Solutions',
    description: 'Discover high-quality bidet solutions from BidetNY.',
  };
  const currentMetadata = { ...defaultMetadata, ...routeMetadata };
  const pageTitle = currentMetadata.title ? `${currentMetadata.title} | BidetNY` : defaultMetadata.title;

  const metaTags = [];
  // React Router v6's meta function expects an object with key-value pairs for tags,
  // or an object with a 'title' key for the page title.
  if (pageTitle) metaTags.push({ title: pageTitle });
  if (currentMetadata.description) metaTags.push({ name: 'description', content: currentMetadata.description });
  if (currentMetadata.keywords && currentMetadata.keywords.length > 0) metaTags.push({ name: 'keywords', content: currentMetadata.keywords.join(', ') });
  
  // Open Graph Tags
  metaTags.push({ property: 'og:title', content: currentMetadata.ogTitle || currentMetadata.title || defaultMetadata.title });
  metaTags.push({ property: 'og:description', content: currentMetadata.ogDescription || currentMetadata.description || defaultMetadata.description });
  if (currentMetadata.ogImage) metaTags.push({ property: 'og:image', content: currentMetadata.ogImage });
  // Add other OG tags as needed, e.g., og:type, og:url
  metaTags.push({ property: 'og:type', content: 'website' }); // Example
  // You might want to dynamically set og:url based on the current route
  // metaTags.push({ property: 'og:url', content: typeof window !== 'undefined' ? window.location.href : '' });


  return metaTags;
}


// Separate component to use the OrderContext
function AppContent() {
  const { bidetModalOpen } = useOrder();

  return (
    <>
      <StandardPage>
        <Outlet />
      </StandardPage>

      { bidetModalOpen && (
        <BidetModal />
      )}
    </>
  );
}

// Standalone type for Layout props
export interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />{/* This will now render tags from the new meta function */}
        <Links />
        {/* <Helmet> was here, removed </Helmet> */}
      </head>
      <body>
        {import.meta.env.VITE_ENV === 'staging' ? (
          <StagingPasswordWall>{children}</StagingPasswordWall>
        ) : (
          children
        )}
        <ScrollRestoration />
        <Scripts />

        <script
          id="react-google-places-autocomplete"
          type="text/javascript"
          src={`https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_PLACES_API_KEY}&libraries=places,geometry`}
        ></script>

        { import.meta.env.VITE_ENV === 'production' && (
          <>
            <script async src={`https://www.googletagmanager.com/gtag/js?id=${import.meta.env.VITE_GOOGLE_ANALYTICS_TAG}`}></script>
            <script>
              {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${import.meta.env.VITE_GOOGLE_ANALYTICS_TAG}');
              `}
            </script>
          </>
        ) }
      </body>
    </html>
  );
}

export default function App() {
  useEffect(() => {
    const key = 'session-last-loaded';
    const lastLoaded = localStorage.getItem(key);
    let shouldWipe = false;
    if (lastLoaded) {
      const lastLoadedDate = new Date(lastLoaded);
      const now = new Date();
      const diffMs = now.getTime() - lastLoadedDate.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      if (diffHours > 6) {
        shouldWipe = true;
        localStorage.clear();
      }
    }
    const nowString = new Date().toISOString();
    localStorage.setItem(key, nowString);
  }, []);

  return (
    <SiteConfigProvider>
      <ProductProvider>
        <OrderProvider>
          <AppContent />
        </OrderProvider>
      </ProductProvider>
    </SiteConfigProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details = error.status === 404
      ? 'The requested page could not be found.'
      : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
